import React, { createContext, useContext, useMemo, useState } from "react";

interface BreadcrumbLabelContextValue {
    dynamicLabels: Record<string, string>;
    setDynamicLabel: (path: string, label: string) => void;
    clearDynamicLabel: (path: string) => void;
}

const BreadcrumbLabelContext = createContext<
    BreadcrumbLabelContextValue | undefined
>(undefined);

export function BreadcrumbLabelProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const [dynamicLabels, setDynamicLabels] = useState<Record<string, string>>({});

    const value = useMemo<BreadcrumbLabelContextValue>(
        () => ({
            dynamicLabels,
            setDynamicLabel: (path: string, label: string) => {
                setDynamicLabels((prev) => {
                    if (prev[path] === label) {
                        return prev;
                    }
                    return { ...prev, [path]: label };
                });
            },
            clearDynamicLabel: (path: string) => {
                setDynamicLabels((prev) => {
                    if (!(path in prev)) {
                        return prev;
                    }

                    const next = { ...prev };
                    delete next[path];
                    return next;
                });
            },
        }),
        [dynamicLabels],
    );

    return (
        <BreadcrumbLabelContext.Provider value={value}>
            {children}
        </BreadcrumbLabelContext.Provider>
    );
}

export function useBreadcrumbLabels() {
    const context = useContext(BreadcrumbLabelContext);

    if (!context) {
        throw new Error(
            "useBreadcrumbLabels must be used within BreadcrumbLabelProvider",
        );
    }

    return context;
}
